import {Capacitor} from '@capacitor/core';
import {getVisitaOfflineEntry} from './visitaOfflineQueue.js';
import {getVisitaOfflineOperationsByVisita} from './visitaOfflineOperationQueue.js';
import {withVisitaLocalLock,hasPendingVisitaStructure,requestVisitaSync} from './visitaLocalFirst.js';
import {reconcileCausalMergeAck,VISITA_MERGE_BASE_ACKNOWLEDGED} from './visitaMergeCausalAck.js';
import {OFFLINE_STORES,getOfflineStorageDriver} from './offlineStorageAdapter.js';
import {mergeBaseKey,syncMergeProposal,canonicalMerge,verifyProposalReceipt,verifyPreparedMergeBase,verifyAutomaticReceipt} from './visitaMergeJournal.js';
export async function reconcileMergeJournal(status) {
    const driver=getOfflineStorageDriver(),rows=await driver.getAll(OFFLINE_STORES.CONTEXTS,row=>row.kind==='merge_proposal',
        {userId:status.user_id,contexts:{visitaId:status.visita_id,empresaId:status.empresa_id,kinds:['merge_proposal']}});
    for(const row of rows){
        if(row.kind!=='merge_proposal'||row.userId!==status.user_id||row.empresaId!==status.empresa_id||row.visitaId!==status.visita_id)continue;
        const item=status.proposals?.find(p=>p.proposal_uuid===JSON.parse(row.literal).proposal_uuid&&p.client_revision===row.revisionId);
        if(!item)continue;
        verifyProposalReceipt(item.receipt,row);
        if(row.state==='resolution_applied'&&!item.decision)continue;
        if(item.automatic&&!item.decision){
            const automatic=verifyAutomaticReceipt(item.automatic,row);
            if(row.automatic?.complete&&!automatic.complete||Number(row.automatic?.responses_version)>automatic.responses_version)continue;
            const state=automatic.complete?(row.state==='automatic_applied'?'automatic_applied':'automatic_copy_pending'):'automatic_partial';
            if(row.state===state&&canonicalMerge(row.automatic)===canonicalMerge(automatic)&&row.receipt)continue;
            const next={...row,receipt:item.receipt,automatic,state,version:crypto.randomUUID()};
            if(driver.publishVisitaContext){try{await driver.publishVisitaContext(next,row.version);}catch{/* Otra lectura conserva su publicación; no reemplazarla. */}}
            else await driver.mutate(OFFLINE_STORES.CONTEXTS,row.id,current=>current?.version===row.version?next:current);
            continue;
        }
        if(row.state==='resolution_applied'&&!item.decision)continue;
        if(item.decision&&(!['merge_applied','merge_no_changes'].includes(item.decision.effect)||item.decision.proposal_uuid!==item.proposal_uuid||item.decision.approved!==false))throw Error('El resultado de la propuesta requiere revisión.');
        const state=item.decision?'resolution_applied':item.state;
        if(!['proposal_received','conflict_pending','resolution_applied'].includes(state))continue;
        if(row.state===state && canonicalMerge(row.receipt||null)===canonicalMerge(item.receipt)
            && canonicalMerge(row.decision||null)===canonicalMerge(item.decision||null))continue;
        const next={...row,state,receipt:item.receipt,...(item.decision?{decision:item.decision}:{}),version:crypto.randomUUID()};
        if(typeof driver.publishVisitaContext==='function'){
            try{await driver.publishVisitaContext(next,row.version);}catch{/* Otra pestaña conservó una versión más reciente. */}
        }else await driver.mutate(OFFLINE_STORES.CONTEXTS,row.id,current=>current?.version===row.version?next:current);
    }
}
export async function saveMergeBase(prepared,expected) {
    await verifyPreparedMergeBase(prepared,expected);
    const driver=getOfflineStorageDriver(), id=mergeBaseKey(prepared.user_id,prepared.visita_id);
    const previous=await driver.get(OFFLINE_STORES.CONTEXTS,id),incoming=JSON.parse(prepared.base.canonical_payload);
    const covers=current=>{
        if(!current?.prepared)return false;
        const body=JSON.parse(current.prepared.base.canonical_payload);
        return body.responses_version>incoming.responses_version||body.responses_version===incoming.responses_version
            &&body.form_revision===incoming.form_revision&&current.prepared.base.snapshot_hash===prepared.base.snapshot_hash;
    };
    if(previous?.prepared&&JSON.parse(previous.prepared.base.canonical_payload).responses_version>incoming.responses_version)return;
    const row={id,kind:'merge_base',userId:prepared.user_id,empresaId:prepared.empresa_id,visitaId:prepared.visita_id,
        scope:`merge:${prepared.empresa_id}:${prepared.user_id}`,version:crypto.randomUUID(),templateVersion:JSON.parse(prepared.base.canonical_payload).form_revision,prepared};
    if(driver.publishVisitaContext){
        try{await driver.publishVisitaContext(row,previous?.version||'');}
        catch(error){if(covers(await driver.get(OFFLINE_STORES.CONTEXTS,id)))return;throw error;}
    }else await driver.mutate(OFFLINE_STORES.CONTEXTS,id,current=>covers(current)&&JSON.parse(current.prepared.base.canonical_payload).responses_version>incoming.responses_version?current:row);
    const saved=await driver.get(OFFLINE_STORES.CONTEXTS,id);
    if(canonicalMerge(saved)!==canonicalMerge(row)&&!covers(saved))throw Error('No se pudo verificar la base local del piloto.');
}
export async function getMergeBase(userId,visitaId) {
    return (await getOfflineStorageDriver().get(OFFLINE_STORES.CONTEXTS,mergeBaseKey(userId,visitaId)))?.prepared;
}
async function reconcileConfirmedBase(entry,isCurrent) {
    const driver=getOfflineStorageDriver();
    const rows=(await driver.getAll(OFFLINE_STORES.CONTEXTS,null,{userId:entry.userId,contexts:{visitaId:entry.visitaId}}))
        .filter(row=>row.userId===entry.userId&&row.visitaId===entry.visitaId&&row.empresaId===entry.payload._merge.empresa_id);
    const copies=[...rows.filter(row=>row.kind==='merge_confirmed_copy'&&row.application?.complete),
        ...rows.filter(row=>row.kind==='merge_proposal'&&row.receipt&&row.automatic?.complete&&row.automatic.applied_base)
            .map(row=>({...row,application:row.automatic,context:null}))]
        .sort((a,b)=>b.application.responses_version-a.application.responses_version);
    const copy=copies.find(copy=>rows.some(row=>row.kind==='merge_proposal'&&row.revisionId===copy.application.client_revision
        &&row.original?.payload?._merge?.base?.payload_hash===entry.payload._merge.base.payload_hash));
    if(!copy)return null;
    const sentRow=rows.find(row=>row.kind==='merge_proposal'&&row.revisionId===copy.application.client_revision);
    const result=await reconcileCausalMergeAck({sentRow,context:copy.context,automatic:copy.application,driver,stores:OFFLINE_STORES,isCurrent,
        readEntry:()=>getVisitaOfflineEntry({userId:entry.userId,visitaId:entry.visitaId}),
        withLock:task=>withVisitaLocalLock(entry.userId,entry.visitaId,async()=>{
            if(hasPendingVisitaStructure(await getVisitaOfflineOperationsByVisita(entry.visitaId,entry.userId),entry.visitaId))return {eligible:false};
            return task();
        }),
        onBase:async detail=>{
            await saveMergeBase(detail.prepared,{userId:entry.userId,empresaId:copy.empresaId,visitaId:entry.visitaId});
            if(!isCurrent())return;
            if(typeof window!=='undefined')window.dispatchEvent(new CustomEvent(VISITA_MERGE_BASE_ACKNOWLEDGED,
                {detail:{...detail,userId:entry.userId,empresaId:copy.empresaId,visitaId:entry.visitaId}}));
        }});
    if(result.rebased&&isCurrent()){
        if(typeof window!=='undefined')window.dispatchEvent(new CustomEvent('visita-offline-queue-changed'));
        requestVisitaSync({immediate:true,newCycle:false});
    }
    return result;
}
export async function flushMergeDraft(entry,send,{remove,isCurrent=()=>true,allowCausalRebase=true}) {
    if(!entry.payload?._merge)return null;
    if(allowCausalRebase){
        const recovered=await reconcileConfirmedBase(entry,isCurrent);
        if(recovered?.rebased){entry=recovered.entry;requestVisitaSync({newCycle:false});}
    }
    const result=await syncMergeProposal({entry,prepared:entry.payload._merge,driver:getOfflineStorageDriver(),stores:OFFLINE_STORES,send,remove,isCurrent,
        origin:Capacitor.isNativePlatform()?'android':'web',onStatus:reconcileMergeJournal});
    if(result.automaticApplied)await reconcileConfirmedBase(entry,isCurrent);
    else if(allowCausalRebase){
        const recovered=await reconcileConfirmedBase(entry,isCurrent);
        if(recovered?.rebased)return flushMergeDraft(recovered.entry,send,{remove,isCurrent,allowCausalRebase:false});
    }
    return result;
}
